import { never } from "@vendrinc/frontend-ts";
import * as Zod from "zod";
import * as PriceEstimateTypes from "~/domain/priceEstimate/types";
import * as AnswersDomain from "~/domain/privateOffers/answers";

export const elementTypeDecoder = Zod.union([
  Zod.literal("link"),
  Zod.literal("button"),
  Zod.literal("dropdown"),
]);

export type ElementType = Zod.infer<typeof elementTypeDecoder>;

export const elementDecoder = Zod.object({
  element_text: Zod.string(),
  element_location: Zod.string(),
  element_type: elementTypeDecoder,
  page: Zod.string(),
});

export type Element = Zod.infer<typeof elementDecoder>;

export const requestStartedVia = Zod.union([
  Zod.literal("Price Estimate CTA"),
  Zod.literal("Form Fill"),
  Zod.literal("Open Price Form"),
]);

export const unstructuredAnswerDecoder = Zod.union([
  Zod.object({
    question: Zod.string(),
    boolAnswer: Zod.boolean().optional(),
  }),
  Zod.object({
    question: Zod.string(),
    textAnswer: Zod.string().optional(),
  }),
  Zod.object({
    question: Zod.string(),
    dateAnswer: Zod.string().optional(),
  }),
  Zod.object({
    question: Zod.string(),
    floatAnswer: Zod.number().optional(),
  }),
  Zod.object({
    question: Zod.string(),
    documentAnswer: Zod.object({
      backofficeDocumentId: Zod.string(),
      backofficeDocumentReferenceId: Zod.string().optional(),
    }).optional(),
  }),
]);
export const unstructuredAnswersDecoder = Zod.array(unstructuredAnswerDecoder);

export type RequestStartedVia = Zod.infer<typeof requestStartedVia>;

export const requestFormLocation = Zod.union([
  Zod.literal("listing page"),
  Zod.literal("buyer guide"),
  Zod.literal("homepage"),
  Zod.literal("price check"),
  Zod.literal("partner"),
]);

export type RequestFormLocation = Zod.infer<typeof requestFormLocation>;

export const requestFormStartedDecoder = Zod.object({
  product_id: Zod.string(),
  product_name: Zod.string().nullable(),
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
  supplier_is_vendr_verified: Zod.boolean(),
  supplier_is_aws_eligible: Zod.boolean(),
  has_spoken_to_supplier: Zod.boolean().nullish(),
  buying_timeline: Zod.string().nullish(),
  aws_opted_in: Zod.boolean().nullish(),
  vendr_verified_opted_in: Zod.boolean().nullish(),
  request_type: AnswersDomain.requestTypeDecoder.nullish(),
  app_name: Zod.string().nullish(),
  partner_id: Zod.string().nullish(),
  partner_name: Zod.string().nullish(),
  started_via: requestStartedVia.nullish(),
  deliverable_data_coverage: Zod.array(Zod.string()).nullish(),
  request_form_location: requestFormLocation.nullish(),
  referring_request_id: Zod.string().nullish(),
});

export type RequestFormStarted = Zod.infer<typeof requestFormStartedDecoder>;

export const requestStepCompletedDecoder = Zod.intersection(
  requestFormStartedDecoder,
  Zod.object({
    step_name: Zod.string(),
    user_is_authorized_for_purchase: Zod.string().nullable(),
    step_number: Zod.number().nullable(),
    answers: AnswersDomain.answersDecoder,
  }),
);

export type RequestStepCompleted = Zod.infer<
  typeof requestStepCompletedDecoder
>;

export const requestFormStartedBEDecoder = Zod.object({
  request_type: AnswersDomain.requestTypeDecoder.nullish(),
  started_via: requestStartedVia.nullish(),
  request_form_location: requestFormLocation.nullish(),
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
  product_id: Zod.string().nullish(),
});

export const requestFormUpdatedBEDecoder = Zod.intersection(
  requestFormStartedBEDecoder,
  Zod.object({
    purchase_type: PriceEstimateTypes.purchaseTypeSchema.nullish(),
    term_length: PriceEstimateTypes.termLengthSchema.nullish(),
    answers: PriceEstimateTypes.answersSchema.nullish(),
  }),
);

export const requestStepCompletedBEDecoder = Zod.intersection(
  requestFormUpdatedBEDecoder,
  Zod.object({
    step_name: Zod.string(),
    step_number: Zod.number().nullable(),
    answers: PriceEstimateTypes.answersSchema.nullish(),
  }),
);

export const requestFormSubmittedBEDecoder = Zod.intersection(
  requestFormUpdatedBEDecoder,
  Zod.object({
    logged_in: Zod.boolean().nullish(),
    answers: PriceEstimateTypes.answersSchema.nullish(),
  }),
);

export const marketplaceListingViewed = Zod.object({
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
  negotiations_handled: Zod.number().nullish(),
  unique_purchasers: Zod.number().nullish(),
  avg_contract_value: Zod.string().nullish(),
  avg_savings: Zod.string().nullish(),
  has_product_details: Zod.boolean().nullish(),
  has_competitors: Zod.boolean().nullish(),
  has_negotiation_insights: Zod.boolean().nullish(),
  has_community_insights: Zod.boolean().nullish(),
  has_security_and_compliance: Zod.boolean().nullish(),
  index_for_search: Zod.boolean().nullish(),
});

export type MarketplaceListingViewed = Zod.infer<
  typeof marketplaceListingViewed
>;

export const listItemSelected = Zod.object({
  list_type: Zod.string(),
  supplier_is_vendr_verified: Zod.boolean(),
  supplier_position: Zod.number(),
  supplier_slug: Zod.string(),
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
});

export type ListItemSelected = Zod.infer<typeof listItemSelected>;

// I don't think we ever search at a different level, right?
const searchType = Zod.enum(["product", "price_check"]);

export const searchFocused = Zod.object({
  focus_method: Zod.enum(["auto", "click"]),
  search_type: searchType,
});

export type SearchFocused = Zod.infer<typeof searchFocused>;

export const searchStarted = Zod.object({
  search_type: searchType,
  search_query: Zod.string(),
});

export type SearchStarted = Zod.infer<typeof searchStarted>;

export const searchItemSelected = Zod.object({
  price_check_id: Zod.string().optional(),
  price_check_type: Zod.enum(["Analysis", "Comparison"]).optional(),
  price_check_title: Zod.string().optional(),
  product_id: Zod.string().optional(),
  product_name: Zod.string().optional(),
  search_type: searchType,
  selection_method: Zod.enum(["click", "return"]).nullish(),
  selection_position: Zod.number(),
  selection_type: searchType.nullish(),
  supplier_id: Zod.string().nullish(),
  supplier_name: Zod.string().nullish(),
  // Fields required for Algolia/Segment Integration
  // https://www.algolia.com/doc/guides/sending-events/connectors/segment/
  index: Zod.string().optional(),
  objectID: Zod.string(),
  queryID: Zod.string().optional(),
});

export type SearchItemSelected = Zod.infer<typeof searchItemSelected>;

export const identifyDecoder = Zod.object({
  userId: Zod.string(),
  traits: Zod.object({
    email: Zod.string(),
    domain: Zod.string().nullish(),
    role: Zod.string().nullish(),
  }),
});

export type Identify = Zod.infer<typeof identifyDecoder>;

export const aliasDecoder = Zod.object({
  userId: Zod.string(),
  anonymousId: Zod.string(),
});

export type Alias = Zod.infer<typeof aliasDecoder>;

export const featureFlagEvaluationDecoder = Zod.object({
  key: Zod.string(),
  value: Zod.any(),
});

export type FeatureFlagEvaluation = Zod.infer<
  typeof featureFlagEvaluationDecoder
>;

export const experimentStartedDecoder = Zod.object({
  "Experiment name": Zod.string(),
  "Variant name": Zod.string(),
});

export const deliverableDataCoverageDecoder = Zod.enum([
  "SKU Coverage (High)",
  "SKU Coverage (Low)",
  "Product Coverage",
  "Company Coverage",
  "Basic Metadata",
]);

export const buyerRequestViewedBeExtras = {
  is_impersonation: Zod.boolean().optional(),
  answers: unstructuredAnswersDecoder.optional(),
  aws_opted_in: Zod.boolean().optional(),
  vendr_verified_opted_in: Zod.boolean().optional(),
  price_check_percentile: Zod.number().optional(),
  deliverable_data_coverage: deliverableDataCoverageDecoder.optional(),
  quote_annual_value: Zod.number().optional(),
  quote_total_value: Zod.number().optional(),
};

export const buyerRequestViewedBeExtrasObject = Zod.object(
  buyerRequestViewedBeExtras,
);

export type BuyerRequestViewedBeExtras = Zod.infer<
  typeof buyerRequestViewedBeExtrasObject
>;

export const priceCheckInfoScreenViewedDecoder = Zod.object({
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
});

export const buyerRequestViewedDecoder = Zod.object({
  product_id: Zod.string().optional(),
  product_name: Zod.string().optional(),
  purchase_type: AnswersDomain.typeOfPurchaseDecoder,
  task_id: Zod.string().nullable(), // backoffice task id
  request_id: Zod.string(), // ironzion task id
  request_status: Zod.string(), // ironzion status
  request_type: AnswersDomain.requestTypeDecoder,
  supplier_id: Zod.string(),
  supplier_is_aws_eligibile: Zod.boolean().nullable(),
  supplier_is_vendr_verified: Zod.boolean(),
  supplier_name: Zod.string(),
  buyer_organization_type: Zod.string(),
  buyer_organization_id: Zod.string().nullish(),
  backoffice_state: Zod.string().nullable(),
}).extend(buyerRequestViewedBeExtras);

export type BuyerRequestViewed = Zod.infer<typeof buyerRequestViewedDecoder>;
export const estimateRatingDetailsDecoder = Zod.object({
  type: Zod.literal("price_estimate"),
  rating: Zod.number(),
  feedback: Zod.array(Zod.string()),
});

export const ratingDetailsDecoder = Zod.discriminatedUnion("type", [
  estimateRatingDetailsDecoder,
]);

export const buyerRequestRatedDecoder = Zod.object({
  product_id: Zod.string().optional(),
  product_name: Zod.string().optional(),
  purchase_type: AnswersDomain.typeOfPurchaseDecoder,
  rating_details: ratingDetailsDecoder,
  request_id: Zod.string(),
  request_rating: Zod.number(),
  request_status: Zod.string(),
  request_type: AnswersDomain.requestTypeDecoder,
  supplier_id: Zod.string(),
  supplier_is_aws_eligibile: Zod.boolean(),
  supplier_is_vendr_verified: Zod.boolean(),
  supplier_name: Zod.string(),
});

export const toolTipDisplayedDecoder = Zod.object({
  tool_tip_context: Zod.string(),
  tool_tip_location: Zod.string(),
  tool_tip_object: Zod.string(),
  tool_tip_text: Zod.string(),
});

const buyerGuideViewedDecoder = Zod.object({
  supplier_id: Zod.string(),
});

export const sharedRequestViewedDecoder = Zod.object({
  request_type: AnswersDomain.requestTypeDecoder.nullish(),
  request_id: Zod.string(), // ironzion task id
  task_id: Zod.string().nullish(), // backoffice task id
  buyer_organization_id: Zod.string(),
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
});
export type SharedRequestViewed = Zod.infer<typeof sharedRequestViewedDecoder>;

export type ExperimentStarted = Zod.infer<typeof experimentStartedDecoder>;

export const buyerRequestUpdatedDecoder = Zod.object({
  deliverable_data_coverage: deliverableDataCoverageDecoder,
  new_quantity: Zod.number().optional(),
  new_sku_id: Zod.string().optional(),
  previous_quantity: Zod.number().optional(),
  previous_request_status: Zod.string(),
  previous_sku_id: Zod.string().optional(),
  product_id: Zod.string().optional(),
  product_name: Zod.string().optional(),
  purchase_type: AnswersDomain.typeOfPurchaseDecoder,
  request_id: Zod.string(), // ironzion task id
  request_status: Zod.string(), // ironzion status
  request_type: AnswersDomain.requestTypeDecoder,
  supplier_id: Zod.string(),
  supplier_is_aws_eligibile: Zod.boolean().nullable(),
  supplier_is_vendr_verified: Zod.boolean(),
  supplier_name: Zod.string(),
}).extend(buyerRequestViewedBeExtras);

export const purchaseScopeSubmittedDecoder = Zod.object({
  scope_data: Zod.unknown(),
  source: Zod.string().nullish(),
  purchase_scope_id: Zod.string(),
  supplier_id: Zod.string(),
  supplier_name: Zod.string(),
});

export const buyWithAwsStartedDecoder = Zod.object({
  supplier_name: Zod.string(),
  supplier_id: Zod.string(),
});

export const continuePurchaseClickedDecoder = Zod.object({
  supplier_name: Zod.string(),
  supplier_id: Zod.string(),
  purchase_scope_id: Zod.string().uuid(),
});

const actionLinkDecoder = Zod.object({
  id: Zod.string(),
  type: Zod.enum(["PriceEstimate"]),
  partner_id: Zod.string(),
  name: Zod.string(),
  mode: Zod.union([Zod.literal("Live"), Zod.literal("Test")]),
  origin: Zod.string(),
});

const actionLinkButtonRenderedDecoder = actionLinkDecoder.extend({
  display_name: Zod.string().optional(),
  action_label: Zod.string().optional(),
  company_id: Zod.string().uuid().nullish(),
  product_id: Zod.string().uuid().nullish(),
  props_company_id: Zod.string().nullish(),
  props_product_id: Zod.string().nullish(),
});

const actionLinkModalOpenedDecoder = actionLinkDecoder.extend({
  company_id: Zod.string().uuid(),
  product_id: Zod.string().uuid().nullish(),
});

const actionLinkModalClosedDecoder = actionLinkModalOpenedDecoder.extend({
  step: Zod.string().nullish(),
});

const actionLinkProductTierChangedDecoder = actionLinkDecoder.extend({
  company_id: Zod.string().uuid(),
  product_id: Zod.string().uuid(),
  product_tier_id: Zod.string().nullish(),
});

const actionLinkQuantityChangedDecoder =
  actionLinkProductTierChangedDecoder.extend({
    quantity: Zod.number(),
  });

const actionLinkStepChangedDecoder = actionLinkDecoder.extend({
  step: Zod.string(),
});

const actionLinkPriceEstimateSubmittedDecoder = actionLinkDecoder.extend({
  step: Zod.string(),
  userId: Zod.string().uuid(),
  price_estimate_id: Zod.string(),
  company_id: Zod.string().uuid().nullish(),
  product_id: Zod.string().uuid().nullish(),
});

const actionLinkEventTypes = [
  Zod.object({
    type: Zod.literal("Action Link Button Rendered"),
    properties: actionLinkButtonRenderedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Action Link Modal Opened"),
    properties: actionLinkModalOpenedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Action Link Modal Closed"),
    properties: actionLinkModalClosedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Action Link Product Tier Changed"),
    properties: actionLinkProductTierChangedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Action Link Quantity Changed"),
    properties: actionLinkQuantityChangedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Action Link Step Changed"),
    properties: actionLinkStepChangedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Action Link Price Estimate Submitted"),
    properties: actionLinkPriceEstimateSubmittedDecoder,
  }),
];

export const priceCheckContractAnalysisDeliveredDecoder = Zod.object({
  request_type: Zod.literal("Contract Analysis"),
  request_id: Zod.string(),
  document_id: Zod.string().uuid(),
  file_name: Zod.string(),
  file_size: Zod.number(),
  file_type: Zod.string(),
  negotiation_potential: Zod.enum(["high", "medium", "low"]),
  organization_id: Zod.string(),
  organization_name: Zod.string().nullable(),
  price_category: Zod.enum(["high", "medium", "low"]),
  price_percentile: Zod.number().nullable(),
  price_total: Zod.number().nullable(),
  products: Zod.array(
    Zod.object({
      id: Zod.string(),
      name: Zod.string(),
    }),
  ),
  status: Zod.literal("Delivered"),
  supplier_id: Zod.string().nullable(),
  supplier_name: Zod.string().nullable(),
  task_id: Zod.string(),
  user_email: Zod.string(),
  user_id: Zod.string(),
});

export const priceCheckContractAnalysisDeliveredV2Decoder = Zod.object({
  request_type: Zod.literal("Contract Analysis"),
  request_id: Zod.string(),
  public_api_company_id: Zod.string().uuid().nullish(),
  public_api_product_id: Zod.string().uuid().nullish(),
  status: Zod.enum(["Pending", "Processing", "Completed", "Failed"]),
  error: Zod.string().nullable(),
  organization_id: Zod.string().nullable(),
  user_id: Zod.string(),
});

export const priceCheckUnitsModifiedDecoder = Zod.object({
  request_type: Zod.literal("Price Comparison"),
  request_id: Zod.string(),
  supplier_id: Zod.string().nullable(),
  supplier_name: Zod.string().nullable(),
  product_id: Zod.string(),
  product_name: Zod.string(),
  quantity_from: Zod.number().nullable(),
  quantity_to: Zod.number(),
});

export const priceCheckContractUploadedDecoder = Zod.object({
  request_type: Zod.literal("Contract Analysis"),
  request_id: Zod.string().uuid(),
  batch_quantity: Zod.number(),
});

export const priceCheckDurationDecoder = Zod.discriminatedUnion(
  "request_type",
  [
    Zod.object({
      duration: Zod.number(),
      request_type: Zod.literal("Price Comparison"),
      request_id: Zod.string(),
      initial_products: Zod.array(
        Zod.object({
          product_id: Zod.string(),
          product_name: Zod.string(),
        }),
      ),
      final_products: Zod.array(
        Zod.object({
          product_id: Zod.string(),
          product_name: Zod.string(),
        }),
      ),
    }),
    Zod.object({
      duration: Zod.number(),
      request_type: Zod.literal("Contract Analysis"),
      request_id: Zod.string(),
      supplier_id: Zod.string().nullable(),
      sku_ids: Zod.array(Zod.string()),
    }),
  ],
);

export const priceCheckProductSelectedDecoder = Zod.object({
  request_type: Zod.literal("Price Comparison"),
  request_id: Zod.string(),
  supplier_id: Zod.string().nullable(),
  supplier_name: Zod.string().nullable(),
  product_id: Zod.string(),
  product_name: Zod.string(),
  existing_products: Zod.array(
    Zod.object({
      product_id: Zod.string(),
      product_name: Zod.string(),
      supplier_id: Zod.string().nullable(),
      supplier_name: Zod.string().nullable(),
    }),
  ),
});

export const priceCheckProductsRemovedDecoder = Zod.object({
  request_type: Zod.literal("Price Comparison"),
  request_id: Zod.string(),
  supplier_id: Zod.string().nullable(),
  supplier_name: Zod.string().nullable(),
  product_id: Zod.string(),
  product_name: Zod.string(),
  has_data: Zod.boolean(),
  existing_products: Zod.array(
    Zod.object({
      product_id: Zod.string(),
      product_name: Zod.string(),
      supplier_id: Zod.string().nullable(),
      supplier_name: Zod.string().nullable(),
    }),
  ),
});

export const priceCheckStartedDecoder = Zod.discriminatedUnion("request_type", [
  Zod.object({
    request_type: Zod.literal("Price Comparison"),
    request_id: Zod.string(),
    products: Zod.array(
      Zod.object({
        fair_price_min: Zod.number().nullable(),
        fair_price_max: Zod.number().nullable(),
        product_id: Zod.string(),
        product_name: Zod.string().nullable(),
        supplier_id: Zod.string().nullable(),
        supplier_name: Zod.string().nullable(),
      }),
    ),
    user_email: Zod.string(),
    quantity: Zod.number(),
  }),
  Zod.object({
    request_type: Zod.literal("Contract Analysis"),
    request_id: Zod.string(),
  }),
]);

export const priceCheckViewedDecoder = Zod.discriminatedUnion("request_type", [
  Zod.object({
    request_type: Zod.literal("Price Comparison"),
    request_id: Zod.string(),
    products: Zod.array(
      Zod.object({
        product_id: Zod.string(),
        product_name: Zod.string().nullable(),
        supplier_id: Zod.string().nullable(),
        supplier_name: Zod.string().nullable(),
      }),
    ),
  }),
  Zod.object({
    request_type: Zod.literal("Contract Analysis"),
    request_id: Zod.string(),
    supplier_id: Zod.string().nullable(),
    products: Zod.array(
      Zod.object({
        sku_id: Zod.string().nullable(),
      }),
    ),
  }),
]);

export const authenticationDisplayedDecoder = Zod.object({
  supplier_id: Zod.string().nullable(),
  supplier_name: Zod.string().nullable(),
});

export const loginDecoder = Zod.object({
  identity: Zod.object({
    userId: Zod.string(),
    anonymousId: Zod.string().nullable(),
    email: Zod.string(),
  }),
});

export const sectionTimeSpentDecoder = Zod.object({
  supplier_id: Zod.string().optional(),
  supplier_name: Zod.string().optional(),
  section_id: Zod.string(),
  time_spent_ms: Zod.number(),
  timestamp: Zod.number(),
  reason: Zod.string().nullish(),
});

// Pricing page event decoders
export const pricingToggleChangedDecoder = Zod.object({
  billing_period: Zod.enum(["annual", "monthly"]),
  user_count: Zod.number(),
});

export const pricingSliderChangedDecoder = Zod.object({
  user_count: Zod.number(),
  billing_period: Zod.enum(["annual", "monthly"]),
  price_per_user: Zod.number(),
});

export const negotiationDealsChangedDecoder = Zod.object({
  autonomous_deals: Zod.number(),
  advisory_deals: Zod.number(),
  estimated_credits: Zod.number(),
  estimated_price: Zod.number(),
});

// Supplier Coverage page event decoders
export const supplierCoverageSearchDecoder = Zod.object({
  search_query: Zod.string(),
  result_count: Zod.number(),
  element_location: Zod.string(),
});

export const supplierCoverageSortDecoder = Zod.object({
  sort_column: Zod.string(),
  sort_direction: Zod.enum(["asc", "desc"]).nullable(),
  element_location: Zod.string(),
});

export const supplierCoverageFilterDecoder = Zod.object({
  filter_key: Zod.string(),
  filter_value: Zod.string(),
  element_location: Zod.string(),
});

export const supplierCoveragePageChangeDecoder = Zod.object({
  page_number: Zod.number(),
  element_location: Zod.string(),
});

export const supplierCoveragePageSizeChangeDecoder = Zod.object({
  page_size: Zod.number(),
  element_location: Zod.string(),
});

export const formSubmittedDecoder = Zod.object({
  email: Zod.string().optional(),
  first_name: Zod.string().optional(),
  form_id: Zod.string(),
  form_name: Zod.string(),
  form_type: Zod.literal("Hubspot"),
  last_name: Zod.string().optional(),
  phone: Zod.string().optional(),
  receive_newsletter: Zod.boolean(),
});

const ruthEventTypes = [
  Zod.object({
    type: Zod.literal("Chat Message Engaged"),
    properties: Zod.object({}),
  }),
  Zod.object({
    type: Zod.literal("Chat Message Submitted"),
    properties: Zod.object({
      message: Zod.string(),
    }),
  }),
  Zod.object({
    type: Zod.literal("Suggestion Selected"),
    properties: Zod.object({
      suggestion: Zod.string(),
    }),
  }),
];

export const eventDecoder = Zod.discriminatedUnion("type", [
  Zod.object({
    type: Zod.literal("alias"),
    properties: aliasDecoder,
  }),
  Zod.object({
    type: Zod.literal("identify"),
    properties: identifyDecoder,
  }),
  Zod.object({
    type: Zod.literal("Element Clicked"),
    properties: elementDecoder,
  }),
  Zod.object({
    type: Zod.literal("Dropdown Changed"),
    properties: elementDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Form Started"),
    properties: requestFormStartedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Step Completed"),
    properties: requestStepCompletedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Form Started - BE"),
    properties: requestFormStartedBEDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Form Updated - BE"),
    properties: requestFormUpdatedBEDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Step Completed - BE"),
    properties: requestStepCompletedBEDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Submitted - BE"),
    properties: requestFormSubmittedBEDecoder,
  }),
  Zod.object({
    type: Zod.literal("Request Submitted"),
    properties: requestFormStartedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Marketplace Listing Viewed"),
    properties: marketplaceListingViewed,
  }),
  Zod.object({
    type: Zod.literal("List Item Selected"),
    properties: listItemSelected,
  }),
  Zod.object({
    type: Zod.literal("Search Focused"),
    properties: searchFocused,
  }),
  Zod.object({
    type: Zod.literal("Search Started"),
    properties: searchStarted,
  }),
  Zod.object({
    type: Zod.literal("Search Item Selected"),
    properties: searchItemSelected,
  }),
  Zod.object({
    type: Zod.literal("Feature Flag Evaluation"),
    properties: featureFlagEvaluationDecoder,
  }),
  Zod.object({
    type: Zod.literal("$experiment_started"),
    properties: experimentStartedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Buyer Request Viewed"),
    properties: buyerRequestViewedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Buyer Request Rated"),
    properties: buyerRequestRatedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Tool Tip Displayed"),
    properties: toolTipDisplayedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Shared Request Viewed"),
    properties: sharedRequestViewedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Buyer Request Updated"),
    properties: buyerRequestUpdatedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Purchase Scope Submitted"),
    properties: purchaseScopeSubmittedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Buy with AWS Started"),
    properties: buyWithAwsStartedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Continue Purchase Clicked"),
    properties: continuePurchaseClickedDecoder,
  }),
  Zod.object({
    type: Zod.literal("6sense Company Details Loaded"),
    properties: Zod.record(Zod.string(), Zod.unknown()),
  }),
  Zod.object({
    type: Zod.literal("Buyer Guide Viewed"),
    properties: buyerGuideViewedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Info Screen Viewed"),
    properties: priceCheckInfoScreenViewedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Units Modified"),
    properties: priceCheckUnitsModifiedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Contract Uploaded"),
    properties: priceCheckContractUploadedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Duration"),
    properties: priceCheckDurationDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Product Selected"),
    properties: priceCheckProductSelectedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Products Removed"),
    properties: priceCheckProductsRemovedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Contract Analysis Delivered"),
    properties: priceCheckContractAnalysisDeliveredDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Contract Analysis Delivered V2"),
    properties: priceCheckContractAnalysisDeliveredV2Decoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Started"),
    properties: priceCheckStartedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Price Check Viewed"),
    properties: priceCheckViewedDecoder,
  }),
  ...actionLinkEventTypes,
  Zod.object({
    type: Zod.literal("Authentication Displayed"),
    properties: authenticationDisplayedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Login"),
    properties: loginDecoder,
  }),
  Zod.object({
    type: Zod.literal("Section Time Spent"),
    properties: sectionTimeSpentDecoder,
  }),
  Zod.object({
    type: Zod.literal("Form Submitted"),
    properties: formSubmittedDecoder,
  }),
  ...ruthEventTypes,
  // Pricing page events
  Zod.object({
    type: Zod.literal("Pricing Toggle Changed"),
    properties: pricingToggleChangedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Pricing Slider Changed"),
    properties: pricingSliderChangedDecoder,
  }),
  Zod.object({
    type: Zod.literal("Negotiation Deals Changed"),
    properties: negotiationDealsChangedDecoder,
  }),
  // Supplier Coverage page events
  Zod.object({
    type: Zod.literal("Supplier Coverage Search"),
    properties: supplierCoverageSearchDecoder,
  }),
  Zod.object({
    type: Zod.literal("Supplier Coverage Sort"),
    properties: supplierCoverageSortDecoder,
  }),
  Zod.object({
    type: Zod.literal("Supplier Coverage Filter"),
    properties: supplierCoverageFilterDecoder,
  }),
  Zod.object({
    type: Zod.literal("Supplier Coverage Page Change"),
    properties: supplierCoveragePageChangeDecoder,
  }),
  Zod.object({
    type: Zod.literal("Supplier Coverage Page Size Change"),
    properties: supplierCoveragePageSizeChangeDecoder,
  }),
]);

export type Event = Zod.infer<typeof eventDecoder>;

export const shouldBeSentToBackendSegmentSource = (
  eventType: Event["type"],
): boolean => {
  switch (eventType) {
    case "Section Time Spent":
    case "Request Submitted":
    case "Action Link Button Rendered":
    case "Action Link Modal Opened":
    case "Action Link Modal Closed":
    case "Action Link Product Tier Changed":
    case "Action Link Quantity Changed":
    case "Action Link Step Changed":
    case "Price Check Info Screen Viewed":
    case "alias":
    case "identify":
    case "Element Clicked":
    case "Dropdown Changed":
    case "Request Form Started":
    case "Request Step Completed":
    case "Request Form Started - BE":
    case "Request Form Updated - BE":
    case "Request Step Completed - BE":
    case "Request Submitted - BE":
    case "Marketplace Listing Viewed":
    case "List Item Selected":
    case "Action Link Price Estimate Submitted":
    case "Search Focused":
    case "Search Started":
    case "Search Item Selected":
    case "Feature Flag Evaluation":
    case "$experiment_started":
    case "Buyer Request Viewed":
    case "Buyer Request Rated":
    case "Tool Tip Displayed":
    case "Buyer Guide Viewed":
    case "Shared Request Viewed":
    case "Buyer Request Updated":
    case "Purchase Scope Submitted":
    case "Buy with AWS Started":
    case "Continue Purchase Clicked":
    case "6sense Company Details Loaded":
    case "Price Check Units Modified":
    case "Price Check Contract Uploaded":
    case "Price Check Duration":
    case "Price Check Product Selected":
    case "Price Check Products Removed":
    case "Price Check Contract Analysis Delivered":
    case "Price Check Contract Analysis Delivered V2":
    case "Price Check Started":
    case "Price Check Viewed":
    case "Authentication Displayed":
    case "Login":
    case "Form Submitted":
    case "Chat Message Submitted":
    case "Chat Message Engaged":
    case "Suggestion Selected":
    case "Pricing Toggle Changed":
    case "Pricing Slider Changed":
    case "Negotiation Deals Changed":
    case "Supplier Coverage Search":
    case "Supplier Coverage Sort":
    case "Supplier Coverage Filter":
    case "Supplier Coverage Page Change":
    case "Supplier Coverage Page Size Change":
      return true;
    default:
      return never(eventType);
  }
};
